'use client';

import Link from 'next/link';
import { ChevronRight, House } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  pathname: string;
  title?: string;
  className?: string;
}

export default function Breadcrumb({ pathname, title, className = '' }: BreadcrumbProps) {
  const items = useBreadcrumbs(pathname, title);
  return (
    <nav aria-label="Fil d'Ariane" className={`flex items-center space-x-2 text-sm ${className}`}>
      <ol className="flex items-center space-x-2">
        {/* Accueil - toujours présent */}
        <li>
          <Link
            href="/"
            className="text-gray-500 hover:text-c2e-blue transition-colors flex items-center"
          >
            <House className="h-4 w-4 mr-1" aria-hidden="true" />
            Accueil
          </Link>
        </li>

        {/* Séparateur après Accueil */}
        <li className="text-gray-400">
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </li>

        {/* Éléments du breadcrumb */}
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {item.href ? (
              <Link
                href={item.href}
                className="text-gray-500 hover:text-c2e-blue transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-gray-900 font-medium" aria-current="page">
                {item.label}
              </span>
            )}

            {/* Séparateur entre les éléments (sauf le dernier) */}
            {index < items.length - 1 && (
              <ChevronRight
                className="h-4 w-4 ml-2 text-gray-400"
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Hook utilitaire pour générer les breadcrumbs automatiquement
export function useBreadcrumbs(pathname: string, title?: string): BreadcrumbItem[] {
  const pathSegments = pathname.split('/').filter(Boolean);

  const breadcrumbs: BreadcrumbItem[] = [];

  // Construire le chemin progressivement
  let currentPath = '';

  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    // Traduire les segments en labels français
    let label = segment;
    switch (segment) {
      case 'events':
        label = 'Événements';
        break;
      case 'blog':
        label = 'Blog';
        break;
      case 'about':
        label = 'À propos';
        break;
      case 'activities':
        label = 'Activités';
        break;
      case 'contact':
        label = 'Contact';
        break;
      case 'join':
        label = 'Rejoindre';
        break;
      case 'gallery':
        label = 'Galerie';
        break;
      default:
        // Pour les slugs dynamiques, utiliser le titre si fourni
        if (index === pathSegments.length - 1 && title) {
          label = title;
        } else {
          // Convertir les slugs en titres lisibles
          label = segment
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
    }

    // Le dernier élément n'a pas de href (page actuelle)
    const href = index === pathSegments.length - 1 ? undefined : currentPath;

    breadcrumbs.push({ label, href });
  });

  return breadcrumbs;
}